import styled, { css, keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Layout Components
export const SectionHeader = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;

export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);
    
    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

     svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Dashboard Components
export const DashboardGridContainer = styled.div`
  margin-bottom: 30px;
`;

export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DashboardCard = styled.div`
  background: var(--surface);
  padding: 30px;
  border-radius: 20px;
  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${(props) => {
      switch (props.$type) {
        case "revenue":
          return "var(--success)";
        case "users":
          return "var(--info)";
        case "courses":
          return "var(--warning)";
        case "orders":
          return "var(--accent)";
        default:
          return "var(--accent)";
      }
    }};
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const CardIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--surface);
  background: ${(props) => {
    switch (props.$type) {
      case "revenue":
        return "var(--success)";
      case "users":
        return "var(--info)";
      case "courses":
        return "var(--warning)";
      case "orders":
        return "var(--accent)";
      default:
        return "var(--accent)";
    }
  }};
`;

export const CardValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

export const CardLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 15px;
`;

// Loading Container
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

// Table Components
export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 180px 150px;
  min-width: 840px;
  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
`;

export const TableHeader = styled.div`
  padding: 15px 12px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "left"};
`;

export const TableRow = styled.div`
  display: contents;
  cursor: pointer;
  &:hover > div {
    background: rgba(124, 14, 51, 0.05);
  }
`;

// Filter Components
export const Filters = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
`;

export const FilterInput = styled.input`
  padding: 12px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-family: inherit;
  max-height: 38px;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;
  outline: none;
  width: 100%;
  max-width: 300px;

  &:focus {
    border-color: var(--accent);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const CourseStatusBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) =>
    props.$active ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)"};
  color: ${(props) => (props.$active ? "#059669" : "#dc2626")};
`;

// Edit Form Components
export const EditForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const EditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${(props) => props.$full && "grid-column: 1 / -1;"}
`;

export const EditLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
`;

export const EditInput = styled.input`
  padding: 10px 14px;
  border: 2px solid var(--accent-light);
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }
`;

export const EditTextArea = styled.textarea`
  padding: 10px 14px;
  border: 2px solid var(--accent-light);
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  outline: none;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }
`;

export const TableCell = styled.div`
  white-space: nowrap;
  padding: 15px 12px;
  border-bottom: 1px solid var(--accent-light);
  display: flex;
  text-align: ${(props) => props.$align || "left"};
  color: ${(props) => (props.$hasSpending ? "var(--success)" : "#000")};
`;

export const LoadingCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
`;

export const EmptyTableCell = styled.td`
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  col-span: 7;
`;

// User and Course Components
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
export const CourseInfo = styled.div``;

export const CourseIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--gradient-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--surface);
`;

export const CourseTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

export const CourseCategory = styled.div`
  font-size: 11px;
  color: var(--text-muted);
`;

// Pagination Components
export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

export const PageInfo = styled.span`
  color: var(--text-muted);
  font-size: 14px;
`;

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: var(--surface);
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  /* Fix za Chrome glitch pri brzom skrolu (providan/pomeren sadržaj) */
  transform: translateZ(0);
  overscroll-behavior: contain;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  border-bottom: 1px solid var(--accent-light);
  padding-bottom: 15px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-muted);
  padding: 5px;
`;

export const ModalGrid = styled.div`
  display: grid;
  gap: 20px;
`;

// Course Details Components
export const CourseDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background-color: var(--background);
  border-radius: 8px;
`;

export const CourseDetailsIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--gradient-primary);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--surface);
  font-size: 24px;
`;

export const CourseDetailsInfo = styled.div``;

export const CourseDetailsTitle = styled.h3`
  margin: 0 0 5px 0;
  font-size: 20px;
  color: var(--text-primary);
`;

export const CourseDetailsId = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 14px;
`;

// Course Info Grid
export const CourseInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

export const InfoField = styled.div``;

export const InfoLabel = styled.label`
  display: block;
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 5px;
  text-transform: uppercase;
  font-weight: 600;
`;

export const InfoValue = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
`;

// Description Section
export const DescriptionSection = styled.div``;

export const DescriptionValue = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
`;

// Stats Grid
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 10px;
`;

export const StatCard = styled.div`
  text-align: center;
  padding: 15px;
  background-color: var(--background);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: var(--accent);
`;

export const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 5px;
`;

// Modal Actions
export const ModalActions = styled.div`
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
